import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { logger } from '../../datadog/logger';
import { UserType } from '../../types';
import { DataChannelMessageType, roomAtom, useSendMessage } from '../../video-sdk';

/**
 * Returns a callback to handle when the completion status of the reflection
 * topic was updated successfully for a patient. This callback sends a data
 * track message with the topic completion status if the patient is in a
 * session, otherwise it does nothing.
 */
export function useHandleReflectionTopicCompletionCallback() {
  const sendDataChannelMessage = useSendMessage();
  const { patientShortId } = useParams<{ patientShortId: string }>();
  const getIsInSession = useAtomCallback(
    useCallback((get) => {
      return !!get(roomAtom);
    }, []),
  );

  return useCallback(
    (topic: { topicShortId: string; completed: boolean }) => {
      // Completing topic outside of a session - noop
      if (!getIsInSession()) {
        return;
      }
      if (!patientShortId) {
        logger.warn('Handling topic completion without patient short ID');
      }
      sendDataChannelMessage({
        type: DataChannelMessageType.REFLECTION_TOPIC_UPDATE,
        topic: {
          ...topic,
          updatedAt: new Date().toISOString(),
        },
        sender: {
          entityType: UserType.CLIENT,
          entityId: patientShortId ?? '',
        },
      });
    },
    [getIsInSession, patientShortId, sendDataChannelMessage],
  );
}
